import { useState } from "react";
import { useApi, apiPost } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingBlock, ErrorState } from "@/components/common/StateViews";
import { SeverityChip } from "@/components/common/SeverityChip";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtDate, fmtNum } from "@/lib/format";
import { Plus, Play, Rocket } from "lucide-react";

export default function Policies() {
  const { data: policies = [], isLoading, error, refetch } = useApi("policies", "/policies");
  const [selected, setSelected] = useState(null);
  const [sim, setSim] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", mode: "enforce", fail_mode: "closed" });

  const runSim = async (p) => {
    setSelected(p); setSim("loading");
    try { setSim(await apiPost(`/policies/${p.id}/simulate`, {})); }
    catch { setSim(null); toast.error("Simulation failed"); }
  };
  const rollout = async (p, stage) => { await apiPost(`/policies/${p.id}/rollout`, { stage }); toast.success(`Policy ${stage === "live" ? "rolled out" : stage}`); refetch(); setSelected(null); };
  const create = async () => {
    if (!form.name) return toast.error("Name required");
    await apiPost("/policies", { ...form, rules: [{ when: { destination_status: ["blocked"] }, then: "block" }], compiles_to: ["gateway", "browser_extension"] });
    toast.success("Draft policy created"); setCreating(false); setForm({ name: "", mode: "enforce", fail_mode: "closed" }); refetch();
  };
  if (error) return <ErrorState />;

  return (
    <div>
      <PageHeader eyebrow="Navitra Guard" title="Policies" subtitle="One policy, many enforcers. Write once; Navitra compiles to gateway, extension, Purview DLP and Entra.">
        <Dialog open={creating} onOpenChange={setCreating}>
          <DialogTrigger asChild><Button size="sm" data-testid="new-policy"><Plus className="h-4 w-4 mr-1.5" />New policy</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">New policy</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Block model access for interns" data-testid="policy-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Mode</Label><Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="enforce">Enforce</SelectItem><SelectItem value="monitor">Monitor</SelectItem></SelectContent></Select></div>
                <div><Label>Fail mode</Label><Select value={form.fail_mode} onValueChange={(v) => setForm({ ...form, fail_mode: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="closed">Closed</SelectItem><SelectItem value="open">Open</SelectItem></SelectContent></Select></div>
              </div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="create-policy-confirm">Create draft</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {isLoading ? <LoadingBlock /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {policies.map((p) => (
            <Card key={p.id} data-testid={`policy-${p.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div><CardTitle className="text-base">{p.name}</CardTitle><p className="mono text-xs text-muted-foreground mt-1">{p.id}@{p.version}</p></div>
                  <SeverityChip level={p.status} />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-1.5">{(p.compiles_to || []).map((c) => <span key={c} className="rounded border px-2 py-0.5 text-[11px] mono text-muted-foreground">{c}</span>)}</div>
                <div className="flex justify-between text-xs text-muted-foreground"><span>Mode: {p.mode} · fail {p.fail_mode}</span><span>Updated {fmtDate(p.updated)}</span></div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => runSim(p)} data-testid={`simulate-${p.id}`}><Play className="h-3.5 w-3.5 mr-1.5" />Simulate</Button>
                  {p.status !== "live" && <Button size="sm" onClick={() => rollout(p, "live")} data-testid={`rollout-${p.id}`}><Rocket className="h-3.5 w-3.5 mr-1.5" />Roll out</Button>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Sheet open={!!selected} onOpenChange={(o) => !o && (setSelected(null), setSim(null))}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          {selected && (<>
            <SheetHeader><SheetTitle className="font-display">Simulation · {selected.name}</SheetTitle><SheetDescription>Impact against the last {sim && sim !== "loading" ? fmtNum(sim.total_events) : "—"} sampled events.</SheetDescription></SheetHeader>
            <div className="mt-5">
              {sim === "loading" ? <LoadingBlock className="h-40" /> : sim && (
                <div className="space-y-4">
                  <div className="rounded-lg border p-4 text-center"><p className="font-display text-3xl">{fmtNum(sim.impacted_events)}</p><p className="text-sm text-muted-foreground">events would change outcome</p></div>
                  <div className="space-y-2">{Object.entries(sim.impact_summary).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between rounded-md border p-2.5"><SeverityChip level={k} /><span className="mono text-sm">{fmtNum(v)}</span></div>
                  ))}</div>
                  {selected.status !== "live" && <Button className="w-full" onClick={() => rollout(selected, "live")}><Rocket className="h-4 w-4 mr-1.5" />Approve & roll out</Button>}
                </div>
              )}
            </div>
          </>)}
        </SheetContent>
      </Sheet>
    </div>
  );
}
